import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AttendanceTime,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  LoadingState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
  kstDateStr,
  kstMonthDayStr,
  kstTimeStr,
  type AttendanceKind,
} from '@biznomi/ui-react'
import { manualReasonLabel, isAbsenceRecord, attendanceTypeLabel } from '@biznomi/types'
import {
  getOrgAttendance,
  getDepartments,
  downloadStandardExcel,
  type AdminAttendanceRecord,
} from '@/lib/api/admin'

const RANGES = [
  { days: 0, label: '오늘' },
  { days: 7, label: '7일' },
  { days: 30, label: '30일' },
] as const

const ALL = '__all__'

function daysAgo(n: number): string {
  return kstDateStr(Date.now() - n * 24 * 60 * 60 * 1000)
}

/** 이번 달 1일 (KST). 급여 주기가 월 단위라 엑셀 기본값도 이번 달이다 */
function monthStart(): string {
  return kstDateStr().slice(0, 8) + '01'
}

export default function AdminAttendance() {
  const [days, setDays] = useState<number>(0)
  const [dept, setDept] = useState<string>(ALL)
  const [search, setSearch] = useState('')

  const [excelOpen, setExcelOpen] = useState(false)
  const [excelFrom, setExcelFrom] = useState(monthStart)
  const [excelTo, setExcelTo] = useState(() => kstDateStr())
  const [excelDept, setExcelDept] = useState(ALL)
  const [downloading, setDownloading] = useState(false)
  const [toast, setToast] = useState('')
  const [toastError, setToastError] = useState(false)

  const from = daysAgo(days)
  const to = kstDateStr()

  const { data: departments } = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: async () => {
      const res = await getDepartments()
      return res.success ? (res.data ?? []) : []
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'attendance', from, to],
    queryFn: async () => {
      const res = await getOrgAttendance({ from, to, take: 1000 })
      if (!res.success) throw new Error(res.error ?? '기록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  // 부서 필터가 서버에 없어서 여기서 좁힌다 (api/admin.ts 주석 참고)
  const rows = useMemo(() => {
    const q = search.trim()
    return (data ?? []).filter((r) => {
      if (dept !== ALL && r.departmentDisplay !== dept) return false
      if (!q) return true
      return Boolean(r.employee?.name?.includes(q) || r.employee?.employeeNumber?.includes(q))
    })
  }, [data, dept, search])

  const byDay = useMemo(() => {
    const map = new Map<string, AdminAttendanceRecord[]>()
    for (const r of rows) {
      const k = kstDateStr(r.timestamp)
      const list = map.get(k)
      if (list) list.push(r)
      else map.set(k, [r])
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [rows])

  const badRange = excelFrom > excelTo

  async function runDownload() {
    setDownloading(true)
    const err = await downloadStandardExcel({
      from: excelFrom,
      to: excelTo,
      departmentId: excelDept === ALL ? undefined : excelDept,
    })
    setDownloading(false)
    setToastError(Boolean(err))
    setToast(err ?? '엑셀을 내려받았습니다')
    if (!err) setExcelOpen(false)
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-bold">출퇴근 기록</h1>
        <div className="flex items-center gap-3">
          <p className="text-sm text-slate-500">{rows.length}건</p>
          <Button size="sm" variant="secondary" onClick={() => setExcelOpen(true)}>
            엑셀
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <Button
            key={r.days}
            size="sm"
            variant={days === r.days ? 'default' : 'secondary'}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </Button>
        ))}

        {/* 부서가 없는 조직(소상공인)에서는 이 필터 자체를 감춘다 — D16 */}
        {(departments?.length ?? 0) > 0 && (
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="h-9 w-40 text-sm">
              <SelectValue placeholder="부서" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>부서 전체</SelectItem>
              {departments!.map((d) => (
                <SelectItem key={d.id} value={d.name}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="이름·사번"
          className="h-9 w-40 text-sm"
        />
      </div>

      {isLoading && <LoadingState />}
      {error && <InfoBox tone="danger">{(error as Error).message}</InfoBox>}

      {!isLoading && !error && byDay.length === 0 && (
        <div className="rounded-card bg-card">
          <EmptyState title="조건에 맞는 기록이 없습니다." icon="search" />
        </div>
      )}

      {byDay.map(([day, records]) => (
        <section key={day}>
          <h2 className="mb-2 text-sm font-bold text-slate-500">{kstMonthDayStr(day)}</h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {records.map((r) => {
              const absence = isAbsenceRecord(r)
              return (
                <li key={r.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="font-medium">{r.employee?.name ?? '-'}</span>
                    {r.employee?.employeeNumber && (
                      <span className="text-xs text-slate-400">{r.employee.employeeNumber}</span>
                    )}
                    {r.departmentDisplay && (
                      <Badge variant="outline">
                        {r.departmentDisplay}
                        {/* 부서를 옮겨도 과거 기록은 그때 부서로 남는다 */}
                        {r.isHistoricalDepartment && ' (당시)'}
                      </Badge>
                    )}
                    {absence ? (
                      <Badge variant="purple">{manualReasonLabel(r.manualReason) || '부재'}</Badge>
                    ) : (
                      <Badge variant={r.type === 'CHECKIN' ? 'info' : 'default'}>
                        {attendanceTypeLabel(r.type)}
                      </Badge>
                    )}
                    {/*
                      🔴 2026-10-21 — 「어디서 찍었나」를 그린다.
                      서버는 `nfcSpot: { name, location }` 을 «계속 주고 있었고» 타입에도 있었는데
                      (`lib/api/attendance.ts:19`) 화면이 한 번도 안 썼다. 그래서 근무지를 여러 곳
                      둔 조직은 「누가·언제」만 보고 「어디서」를 못 봤다.
                      ⚠ 수기·부재 기록엔 근무지가 없다 (찍은 게 아니다) → 그때는 자연히 안 뜬다.
                    */}
                    {r.nfcSpot?.name && (
                      <span className="shrink-0 text-xs text-slate-500">{r.nfcSpot.name}</span>
                    )}
                    {r.isManual && !absence && <Badge variant="outline">수기</Badge>}
                    {r.manualStatus === 'PENDING' && <Badge variant="warning">대기</Badge>}
                  </div>

                  {absence ? (
                    <span className="shrink-0 text-sm text-slate-400">종일</span>
                  ) : (
                    <AttendanceTime
                      at={new Date(r.timestamp)}
                      kind={r.type as AttendanceKind}
                    />
                  )}
                  </div>

                  {/*
                    야간조: 그날 출근 없이 퇴근만 있으면 "안 왔는데 퇴근만 찍었나" 로 오해한다.
                    짝이라고 주장하지 않고 **직전 기록이 무엇이었는지 사실만** 덧붙인다.
                    "전일" 같은 계산을 하지 않는다 — 그저께일 수도 있어서 거짓이 된다.
                  */}
                  {r.prevTag && (
                    <p className="mt-1 text-xs text-slate-400">
                      직전 기록 · {attendanceTypeLabel(r.prevTag.type)}{' '}
                      {kstMonthDayStr(r.prevTag.timestamp)} {kstTimeStr(r.prevTag.timestamp)}
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ))}

      <Dialog open={excelOpen} onOpenChange={setExcelOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>엑셀 내려받기</DialogTitle>
          <DialogDescription>
            집계 시트에 직원별 출근일수가, 상세 시트에 날짜별 출퇴근 시각이 들어갑니다.
          </DialogDescription>

          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <label htmlFor="excel-from" className="sr-only">
                시작일
              </label>
              <Input
                id="excel-from"
                type="date"
                value={excelFrom}
                max={excelTo}
                onChange={(e) => setExcelFrom(e.target.value)}
                className="h-10"
              />
              <span className="shrink-0 text-slate-400">~</span>
              <label htmlFor="excel-to" className="sr-only">
                종료일
              </label>
              <Input
                id="excel-to"
                type="date"
                value={excelTo}
                min={excelFrom}
                onChange={(e) => setExcelTo(e.target.value)}
                className="h-10"
              />
            </div>

            {badRange && (
              <p className="text-sm text-[var(--color-danger)]">시작일이 종료일보다 늦습니다</p>
            )}

            {(departments?.length ?? 0) > 0 && (
              <Select value={excelDept} onValueChange={setExcelDept}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="부서" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>부서 전체</SelectItem>
                  {departments!.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/*
             * 파일을 받아 든 사장이 "비즈노미가 근로시간을 계산해 줬다"고 읽으면
             * 우리가 노무 판단을 한 것이 된다. 엑셀 첫 줄에도 같은 문구가 들어간다
             */}
            <InfoBox tone="info">
              태깅 기록을 센 것입니다. 근로시간 판단·급여 산정은 회사가 별도로 검토해 주세요.
            </InfoBox>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setExcelOpen(false)}>
              취소
            </Button>
            <Button onClick={runDownload} loading={downloading} disabled={badRange}>
              내려받기
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {toast && (
        <Toast
          message={toast}
          variant={toastError ? 'error' : 'success'}
          onClose={() => setToast('')}
        />
      )}
    </div>
  )
}
